import {useCallback, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl';
import {useParams} from 'react-router-dom'
import {Button, Checkbox, Drawer, Form, Input, Select, Spin, Tooltip, Typography} from 'antd'
import {CloseOutlined, DownOutlined, RightOutlined} from '@ant-design/icons'
import InputText from '../common/InputText'
import {getRequest} from '../../services/base'
import useDebouncedValue from '../../utils/common_function'

const PAGE_SIZE = 50

const HarnessLevelsSearchTable = ({
  harness,
  expandedHarnessId,
  onToggleExpand,
  onClearHarness,
  checkedHarnessLevels,
  onSelectHarnessLevel,
  readOnly = false,
}) => {
  const { vehicle } = useParams()
  const intl = useIntl();
  const scrollRef = useRef(null)

  const [levels, setLevels] = useState([])
  const [optionCodes, setOptionCodes] = useState([])
  const [selectedOptionCode, setSelectedOptionCode] = useState('')
  const [optionSearchValue, setOptionSearchValue] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 500)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [offset, setOffset] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [connectorsDrawer, setConnectorsDrawer] = useState({
    visible: false,
    levelId: null,
    levelName: '',
    connectors: [],
    searchQuery: '',
  })

  const isExpanded = expandedHarnessId === harness.id

  // Fetch levels (initial or next page)
  const fetchLevels = useCallback(
    async (currentOffset = 0, search = '', optionCode = '', append = false) => {
      if (currentOffset === 0) {
        setLoading(true)
      } else {
        setLoadingMore(true)
      }

      try {
        const optionCodeParam = optionCode
          ? `&option_code=${optionCode}`
          : ''
        const res = await getRequest(
          `/api/harnesses/harness_levels/${harness.id}/?vehicle=${vehicle}&search=${search}${optionCodeParam}&limit=${PAGE_SIZE}&offset=${currentOffset}`
        )

        // base.js returns a full empty-list shape on failure, so `res?.pagination`
        // is always truthy and the `||` default below never applies. Its key is
        // `nextPageLink`, leaving `pagination.next` undefined - and
        // `undefined !== null` kept hasMore true, so the auto-load-more effect
        // re-fetched forever while the server was unreachable.
        if (res?.status === false) {
          if (!append) setLevels([])
          setHasMore(false)
          return
        }

        const newLevels = res?.levels || []
        const pagination = res?.pagination || { totalCount: 0, next: null }

        setLevels((prev) =>
          append ? [...prev, ...newLevels] : newLevels
        )
        setTotalCount(pagination.totalCount)
        setHasMore(pagination.next !== null)
        setOffset(currentOffset + newLevels.length)
        setOptionCodes([
          { label: 'Any', value: '' },
          ...(res?.optionCodes || []).map((o) => ({
            value: o.id,
            label: o.name,
          })),
        ])
      } catch {
        if (!append) setLevels([])
      } finally {
        // Were bare calls after the try/catch, which the early return in the
        // guard above would have skipped - leaving the spinner up.
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [harness.id, vehicle]
  )

  // Fetch initial levels when expanded
  useEffect(() => {
    if (isExpanded) {
      setLevels([])
      setOffset(0)
      setHasMore(true)
      fetchLevels(0, searchQuery, selectedOptionCode, false)
    } else {
      setConnectorsDrawer({ visible: false, levelId: null, levelName: '', connectors: [], searchQuery: '' })
    }
  }, [isExpanded])

  // Auto-load more levels in readOnly mode when grid doesn't overflow scroll container
  useEffect(() => {
    if (readOnly && !loading && !loadingMore && hasMore && scrollRef.current) {
      const { scrollHeight, clientHeight } = scrollRef.current
      if (scrollHeight <= clientHeight) {
        fetchLevels(offset, searchQuery, selectedOptionCode, true)
      }
    }
  }, [readOnly, levels, loading, loadingMore, hasMore, offset, searchQuery, selectedOptionCode, fetchLevels])

  // Debounced search — auto-search when query changes (after initial load)
  const isInitialExpand = useRef(true)
  useEffect(() => {
    if (!isExpanded) {
      isInitialExpand.current = true
      return
    }
    if (isInitialExpand.current) {
      isInitialExpand.current = false
      return
    }
    setLevels([])
    setOffset(0)
    setHasMore(true)
    fetchLevels(0, debouncedSearchQuery, selectedOptionCode, false)
  }, [debouncedSearchQuery])

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const container = scrollRef.current
    if (!container || loadingMore || !hasMore || loading) return

    const { scrollTop, scrollHeight, clientHeight } = container
    if (scrollHeight - scrollTop - clientHeight < 50) {
      fetchLevels(offset, searchQuery, selectedOptionCode, true)
    }
  }, [offset, searchQuery, selectedOptionCode, loadingMore, hasMore, loading, fetchLevels])

  // Search handler
  const handleSearch = useCallback(
    (value) => {
      setSearchQuery(value)
      setLevels([])
      setOffset(0)
      setHasMore(true)
      fetchLevels(0, value, selectedOptionCode, false)
    },
    [selectedOptionCode, fetchLevels]
  )

  // Option code filter
  const handleOptionCodeChange = useCallback(
    (value) => {
      setSelectedOptionCode(value)
      setOptionSearchValue('')
      setLevels([])
      setOffset(0)
      setHasMore(true)
      fetchLevels(0, searchQuery, value, false)
    },
    [searchQuery, fetchLevels]
  )

  // Select All / Deselect All
  const handleSelectAll = useCallback(() => {
    onSelectHarnessLevel(levels.map((l) => l.id), 'SELECT_ALL')
  }, [levels, onSelectHarnessLevel])

  const handleDeselectAll = useCallback(() => {
    onSelectHarnessLevel([], 'DESELECT_ALL')
  }, [onSelectHarnessLevel])

  const handleViewConnectors = useCallback((e, level) => {
    e.stopPropagation()
    setConnectorsDrawer({
      visible: true,
      levelId: level.id,
      levelName: level.name,
      connectors: level.connector_names,
      searchQuery: '',
    })
  }, [])

  const handleCloseDrawer = useCallback(() => {
    setConnectorsDrawer({ visible: false, levelId: null, levelName: '', connectors: [], searchQuery: '' })
  }, [])

  const handleConnectorSearch = useCallback((e) => {
    setConnectorsDrawer(prev => ({ ...prev, searchQuery: e.target.value }))
  }, [])

  const filteredConnectors = connectorsDrawer.connectors.filter((name) =>
    name.toLowerCase().includes(connectorsDrawer.searchQuery.toLowerCase())
  )

  const isAllChecked =
    levels.length > 0 &&
    levels.every((l) => checkedHarnessLevels.has(l.id))

  const checkedCount = levels.filter((l) =>
    checkedHarnessLevels.has(l.id)
  ).length

  const mappedLevels = readOnly ? levels.filter((l) => checkedHarnessLevels.has(l.id)) : levels

  return (
    <div className="harness-accordion-item">
      {/* Accordion Header */}
      <div
        className="harness-accordion-header"
        onClick={() => onToggleExpand(harness.id)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
          {isExpanded ? <DownOutlined /> : <RightOutlined />}
          <Tooltip title={harness.name || harness.harness_id} placement="topLeft">
            <span className="harness-accordion-title">
              {harness.name || harness.harness_id}
            </span>
          </Tooltip>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="harness-accordion-meta">
            {readOnly
              ? checkedCount > 0 ? `${checkedCount} mapped` : ''
              : totalCount > 0 ? intl.formatMessage({id: 'connectorViews.harnessLevels.levels'}, {count: totalCount}) : ''
            }
          </span>
          {!readOnly && (
            <CloseOutlined
              className="file-remove"
              onClick={(e) => {
                e.stopPropagation()
                onClearHarness(harness, levels)
              }}
            />
          )}
        </div>
      </div>

      {/* Accordion Body */}
      {isExpanded && (
        <div className="harness-accordion-body">
          {/* Search + Filter (always visible, including readOnly) */}
          <div style={{ padding: '12px 12px', display: 'flex', gap: 8 }}>
            <Form
              onFinish={(values) => handleSearch(values.levelSearch || '')}
              initialValues={{levelSearch: searchQuery}}
              style={{ flex: 2 }}
            >
              <InputText
                type="search"
                name="levelSearch"
                placeholder={intl.formatMessage({id: 'connectorViews.harnessLevels.enterLevelName'})}
                isRequired={false}
                wrapperClass="search-wrapper"
                onChange={(value) => {
                  setSearchQuery(value)
                }}
              />
            </Form>
            <Select
              showSearch
              value={selectedOptionCode}
              searchValue={optionSearchValue}
              onSearch={setOptionSearchValue}
              onChange={handleOptionCodeChange}
              options={optionCodes}
              placeholder={intl.formatMessage({id: 'connectorViews.harnessLevels.optionCode'})}
              style={{ flex: 1 }}
              allowClear
              onClear={() => setOptionSearchValue('')}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>

          {/* Select All / Deselect All (hidden in readOnly) */}
          {!readOnly && (
            <div style={{ padding: '0 12px 8px', display: 'flex', gap: 8 }}>
              <Button size="small" onClick={handleSelectAll}>
                {intl.formatMessage({id: 'connectorViews.harnessLevels.selectAll'})}
              </Button>
              <Button size="small" onClick={handleDeselectAll}>
                {intl.formatMessage({id: 'connectorViews.harnessLevels.deselectAll'})}
              </Button>
              {checkedCount > 0 && (
                <Typography.Text type="secondary" style={{ fontSize: 12, alignSelf: 'center' }}>
                  {intl.formatMessage({id: 'connectorViews.harnessLevels.selected'}, {count: checkedCount})}
                </Typography.Text>
              )}
            </div>
          )}

          {/* Levels List with Infinite Scroll */}
          <div
            ref={scrollRef}
            className="harness-levels-scroll"
            onScroll={handleScroll}
            style={{ overflowY: 'auto' }}
          >
            {loading && levels.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center' }}>
                <Spin size="small" />
              </div>
            ) : (
              <>
                {readOnly ? (
                  <div className="harness-levels-grid">
                    {mappedLevels.length === 0 && !loading && (
                      <div style={{ padding: 20, textAlign: 'center', gridColumn: '1 / -1' }}>
                        <Typography.Text type="secondary">
                          {intl.formatMessage({id: 'connectorViews.harnessLevels.noMappedLevels'})}
                        </Typography.Text>
                      </div>
                    )}
                    {mappedLevels.map((level) => (
                      <div key={level.id} className="harness-levels-grid-item">
                        <Typography.Text style={{ fontSize: 13 }}>
                          {level.name}
                        </Typography.Text>
                      </div>
                    ))}
                  </div>
                ) : (
                  levels.map((level) => (
                    <div key={level.id}>
                      <div
                        className={connectorsDrawer.levelId === level.id ? 'level-row-active' : ''}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '6px 12px',
                          gap: 8,
                        }}
                      >
                        <Checkbox
                          checked={checkedHarnessLevels.has(level.id)}
                          onChange={() => onSelectHarnessLevel([level.id])}
                        >
                          <Typography.Text style={{ fontSize: 13 }}>
                            {level.name}
                          </Typography.Text>
                        </Checkbox>
                        {level.connector_names?.length > 0 && (
                          <Button
                            type='link'
                            className="level-connectors-toggle link-button"
                            style={{ marginLeft: 'auto' }}
                            onClick={(e) => handleViewConnectors(e, level)}
                          >
                            {intl.formatMessage({id: 'connectorViews.harnessLevels.connectors'}, {count: level.connector_names.length})}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
                {loadingMore && (
                  <div style={{ padding: 12, textAlign: 'center' }}>
                    <Spin size="small" />
                  </div>
                )}
                {!hasMore && levels.length > 0 && (
                  <div style={{ padding: 12, textAlign: 'center' }}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      {intl.formatMessage({id: 'connectorViews.harnessLevels.allLoaded'})}
                    </Typography.Text>
                  </div>
                )}
                {!loading && levels.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center' }}>
                    <Typography.Text type="secondary">
                      {intl.formatMessage({id: 'connectorViews.harnessLevels.noLevelsFound'})}
                    </Typography.Text>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Connectors Drawer (hidden in readOnly) */}
      {!readOnly && (
        <Drawer
        title={connectorsDrawer.levelName}
        open={connectorsDrawer.visible}
        onClose={handleCloseDrawer}
        width={400}
        placement="right"
        className="connectors-drawer"
        styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' } }}
      >
        <div className="connectors-drawer-top">
          <Input
            autoComplete="off"
            type="search"
            className="connector-mapping-ref-search"
            placeholder={intl.formatMessage({id: 'connectorViews.harnessLevels.drawerSearchPlaceholder'})}
            value={connectorsDrawer.searchQuery}
            onChange={handleConnectorSearch}
          />
          <Typography.Text type="secondary" className="connectors-drawer-count">
            {intl.formatMessage({id: 'connectorViews.harnessLevels.drawerConnectorCount'}, {filtered: filteredConnectors.length, total: connectorsDrawer.connectors.length})}
          </Typography.Text>
        </div>
        <div className="connectors-drawer-list">
          {filteredConnectors.length > 0 ? (
            filteredConnectors.map((name, idx) => (
              <div key={`conn-${idx}`} className="connectors-drawer-item">
                {name}
              </div>
            ))
          ) : (
            <Typography.Text type="secondary" style={{ padding: 16, display: 'block' }}>
              {connectorsDrawer.searchQuery ? intl.formatMessage({id: 'connectorViews.harnessLevels.noConnectorsMatch'}) : intl.formatMessage({id: 'connectorViews.harnessLevels.noConnectorsFound'})}
            </Typography.Text>
          )}
        </div>
      </Drawer>
      )}
    </div>
  )
}

export default HarnessLevelsSearchTable
